import { useEffect, useRef, useCallback } from 'react'

/**
 * Global reference to active Service Worker registration
 * so any component/service can trigger OS notifications.
 */
let activeRegistration = null

/**
 * showServiceWorkerNotification — triggers an OS-level desktop notification via bridge-sw.js.
 * Works even when the browser window is minimized or tab is backgrounded.
 *
 * @param {object} options
 * @param {string} options.title - Notification title
 * @param {string} options.body - Message body
 * @param {string} [options.icon] - Icon path (default: /favicon.ico)
 * @param {string} [options.urlToOpen] - Route to open on click (default: /notifications)
 * @param {string} [options.tag] - Deduplication tag
 * @param {Array}  [options.actions] - Interactive buttons
 */
export async function showServiceWorkerNotification({
  title = 'Bridge Notification',
  body = '',
  icon = '/favicon.ico',
  urlToOpen = '/notifications',
  tag = 'bridge-notification',
  actions = [{ action: 'open', title: 'View' }],
} = {}) {
  try {
    if (!('serviceWorker' in navigator)) return

    if ('Notification' in window && Notification.permission === 'default') {
      try {
        const p = await Notification.requestPermission()
        if (p !== 'granted') return
      } catch (_) {}
    }

    if ('Notification' in window && Notification.permission !== 'granted') {
      return
    }

    let reg = activeRegistration
    if (!reg && 'serviceWorker' in navigator) {
      try {
        reg = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((resolve) => setTimeout(() => resolve(null), 1500)),
        ])
        if (!reg) {
          reg = await navigator.serviceWorker.getRegistration()
        }
      } catch (_) {}
    }

    if (!reg) return

    const notifOptions = {
      body,
      icon: icon || '/favicon.ico',
      badge: '/favicon.ico',
      tag: tag || `bridge-${Date.now()}`,
      requireInteraction: true,
      data: { urlToOpen: urlToOpen || '/notifications' },
    }

    if (reg.showNotification) {
      try {
        await reg.showNotification(title, {
          ...notifOptions,
          actions: actions || [{ action: 'open', title: 'View' }],
        })
      } catch (_) {
        // Fallback without actions in case of OS/browser incompatibility
        await reg.showNotification(title, notifOptions)
      }
    } else if (reg.active) {
      reg.active.postMessage({
        type: 'SHOW_APP_NOTIFICATION',
        title,
        body,
        icon,
        urlToOpen,
        tag,
        actions,
      })
    }
  } catch (err) {
    console.error('[SW Notification] Error dispatching notification:', err)
  }
}

if (typeof window !== 'undefined') {
  window.bridgeTestNotification = showServiceWorkerNotification
}

/**
 * useServiceWorker — registers bridge-sw.js, handles permissions, and routes notification clicks.
 *
 * @param {object}   options
 * @param {Function} [options.onNavigate] Called when user clicks a notification to navigate to a URL.
 * @param {Function} [options.onMessage]  Called when SW posts any message to the page.
 */
export function useServiceWorker({ onNavigate, onMessage } = {}) {
  const onNavigateRef = useRef(onNavigate)
  onNavigateRef.current = onNavigate

  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.warn('[SW] Service Workers not supported in this browser.')
      return
    }

    // Auto-request notification permission on mount if supported
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((res) => {
        console.log('[SW] Desktop notification permission result:', res)
      })
    }

    // Register bridge-sw.js
    navigator.serviceWorker
      .register('/bridge-sw.js', { scope: '/' })
      .then(async (reg) => {
        activeRegistration = reg
        console.log('[SW] bridge-sw.js registered. Scope:', reg.scope)
        try {
          await reg.update()
        } catch (_) {}
      })
      .catch((err) => {
        console.warn('[SW] Registration failed:', err.message)
      })

    // Listen for messages from the SW (e.g. NAVIGATE_TO when notification is clicked)
    const handleSWMessage = (event) => {
      const data = event.data || {}
      if (data.type === 'NAVIGATE_TO' && data.url) {
        if (onNavigateRef.current) {
          onNavigateRef.current(data.url)
        }
      }
      if (onMessageRef.current) {
        onMessageRef.current(data)
      }
    }

    navigator.serviceWorker.addEventListener('message', handleSWMessage)
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSWMessage)
    }
  }, [])

  const triggerNotification = useCallback((params) => {
    return showServiceWorkerNotification(params)
  }, [])

  return { showNotification: triggerNotification }
}
